import { mountBrightcoveBackgroundPlayer } from './shared-brightcove-embed';

export type BackgroundVideoPlaybackState = {
  inView: boolean;
  pageVisible: boolean;
  userPaused: boolean;
};

export function shouldPlayBackgroundVideo({
  inView,
  pageVisible,
  userPaused
}: BackgroundVideoPlaybackState): boolean {
  return inView && pageVisible && !userPaused;
}

export function shouldStartBackgroundVideoPaused(
  isMobile: boolean,
  prefersReducedMotion: boolean
): boolean {
  return isMobile || prefersReducedMotion;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isMobileViewport(): boolean {
  return window.matchMedia('(width < 1024px)').matches;
}

function whenDisconnected(element: HTMLElement, onDisconnect: () => void): void {
  const observer = new MutationObserver(() => {
    if (!element.isConnected) {
      observer.disconnect();
      onDisconnect();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

export default function videoHero(root: HTMLElement): void {
  const promo = root.querySelector<HTMLElement>('[data-video-hero-promo]');
  const promoDismiss = promo?.querySelector<HTMLButtonElement>('[data-promo-card-dismiss]');

  promoDismiss?.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    if (promo) {
      promo.hidden = true;
    }
  });

  const media = root.querySelector<HTMLElement>('[data-video-hero-player]');
  const toggle = root.querySelector<HTMLButtonElement>('[data-video-hero-toggle]');
  const playIcon = root.querySelector<HTMLElement>('[data-video-hero-toggle-play]');
  const pauseIcon = root.querySelector<HTMLElement>('[data-video-hero-toggle-pause]');

  if (!media || !toggle) {
    return;
  }

  let userPaused = shouldStartBackgroundVideoPaused(isMobileViewport(), prefersReducedMotion());
  let inView = false;
  let pageVisible = !document.hidden;
  let isPaused = true;
  let isDisposed = false;
  const lifecycle = new AbortController();
  const { signal } = lifecycle;

  const syncToggle = (): void => {
    toggle.setAttribute(
      'aria-label',
      isPaused ? 'Play background video' : 'Pause background video'
    );
    toggle.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
    root.dataset.videoPlaying = isPaused ? 'false' : 'true';

    if (playIcon) {
      playIcon.hidden = !isPaused;
    }

    if (pauseIcon) {
      pauseIcon.hidden = isPaused;
    }
  };

  const player = mountBrightcoveBackgroundPlayer(media, {
    autoplay: false,
    frameClassName: 'video-hero__video-frame video-js vjs-fill',
    loop: true,
    onPlayingChange: playing => {
      if (isDisposed) {
        return;
      }

      isPaused = !playing;
      syncToggle();
    }
  });

  if (!player) {
    toggle.hidden = true;
    return;
  }

  const playVideo = (): void => {
    void player.play().catch(() => {
      isPaused = true;
      syncToggle();
    });
  };

  const applyPlaybackPolicy = (): void => {
    if (
      shouldPlayBackgroundVideo({
        inView,
        pageVisible,
        userPaused
      })
    ) {
      playVideo();
      return;
    }

    player.pause();
  };

  const visibilityObserver = new IntersectionObserver(
    entries => {
      const entry = entries[0];

      if (!entry || isDisposed) {
        return;
      }

      inView = entry.isIntersecting;
      applyPlaybackPolicy();
    },
    { threshold: 0.25 }
  );

  const teardown = (): void => {
    if (isDisposed) {
      return;
    }

    isDisposed = true;
    lifecycle.abort();
    visibilityObserver.disconnect();
    player.dispose();
  };

  toggle.addEventListener(
    'click',
    () => {
      if (isPaused) {
        userPaused = false;
      } else {
        userPaused = true;
      }

      applyPlaybackPolicy();
    },
    { signal }
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      pageVisible = !document.hidden;
      applyPlaybackPolicy();
    },
    { signal }
  );

  window.addEventListener('pagehide', teardown, { signal });
  visibilityObserver.observe(root);
  whenDisconnected(root, teardown);

  syncToggle();
  applyPlaybackPolicy();
}
